import {
  Crown,
  FolderGit2,
  LogOut,
  MoreHorizontal,
  ShieldCheck,
  Trash2,
  User,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TableCell, TableRow } from "@/components/ui/table";

export interface WorkspaceMemberItem {
  id: string;
  userId: string;
  displayName: string | null;
  avatarUrl?: string | null;
  role: string;
  joinedAt: string | Date;
  projectCount?: number;
}

export function MemberTableRow({
  member,
  isSelf,
  isOwner,
  isUpdatingRole,
  onUpdateRole,
  onRemove,
  onTransfer,
}: {
  member: WorkspaceMemberItem;
  isSelf: boolean;
  isOwner: boolean;
  isUpdatingRole: boolean;
  onUpdateRole: (userId: string, role: "owner" | "member") => void;
  onRemove: (target: {
    userId: string;
    displayName: string | null;
    isSelf: boolean;
  }) => void;
  onTransfer: (target: { userId: string; displayName: string | null }) => void;
}) {
  const initials = (member.displayName || "U")
    .split(" ")
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2.5">
          {member.avatarUrl ? (
            <img
              src={member.avatarUrl}
              alt=""
              className="h-7 w-7 rounded-full object-cover border"
            />
          ) : (
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
              {initials || "U"}
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium">
              {member.displayName || "Unnamed User"}
            </span>
            {isSelf && (
              <Badge
                variant="outline"
                className="rounded-sm text-xs px-1.5 py-0"
              >
                You
              </Badge>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        {isOwner && !isSelf ? (
          <Select
            value={member.role}
            onValueChange={(newRole) => {
              if (newRole === member.role) return;
              onUpdateRole(member.userId, newRole as "owner" | "member");
            }}
            disabled={isUpdatingRole}
          >
            <SelectTrigger className="h-7 w-32 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="owner">
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="h-3 w-3 text-amber-500" />
                  Owner
                </span>
              </SelectItem>
              <SelectItem value="member">
                <span className="flex items-center gap-1.5">
                  <User className="h-3 w-3 text-muted-foreground" />
                  Member
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <Badge
            variant={member.role === "owner" ? "default" : "secondary"}
            className="rounded-sm text-xs capitalize font-normal"
          >
            {member.role === "owner" && (
              <ShieldCheck className="mr-1 h-3 w-3 inline text-amber-400" />
            )}
            {member.role}
          </Badge>
        )}
      </TableCell>
      <TableCell className="hidden text-xs text-muted-foreground md:table-cell">
        {member.role === "owner" ? (
          <span className="text-foreground font-medium">All projects</span>
        ) : (
          <span className="flex items-center gap-1">
            <FolderGit2 className="h-3 w-3 text-muted-foreground" />
            {member.projectCount ?? 0}{" "}
            {member.projectCount === 1 ? "project" : "projects"}
          </span>
        )}
      </TableCell>
      <TableCell className="hidden font-mono text-xs tabular-nums text-muted-foreground md:table-cell">
        {new Date(member.joinedAt).toLocaleDateString()}
      </TableCell>
      <TableCell className="text-right">
        {isSelf ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive gap-1"
            onClick={() =>
              onRemove({
                userId: member.userId,
                displayName: member.displayName,
                isSelf: true,
              })
            }
          >
            <LogOut className="h-3.5 w-3.5" />
            Leave
          </Button>
        ) : isOwner ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Actions for ${member.displayName || "member"}`}
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                onClick={() =>
                  onTransfer({
                    userId: member.userId,
                    displayName: member.displayName,
                  })
                }
              >
                <Crown className="mr-2 h-3.5 w-3.5 text-amber-500" />
                Transfer ownership
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() =>
                  onRemove({
                    userId: member.userId,
                    displayName: member.displayName,
                    isSelf: false,
                  })
                }
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Remove member
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </TableCell>
    </TableRow>
  );
}
